import { isoZ, z } from 'my-zod'
import { match } from 'ts-pattern'
import { AddOnType } from '../../customer-operations/add-on'
import { AddOnProductType } from '../../customer-operations/add-on-product'
import { CustomerOrderBookingMethod } from '../../customer-operations/customer-order'
import { RideInsuranceConfigSchemas } from '../../finance/ride-insurance-config'
import { ProductLine, productLineOptions } from '../../internal'

const partialPaymentFeeCollectionPolicyOptions = ['outfitterFirst', 'proportional'] as const

const feeAgreementModel = z.object({
  outfitterId: z.number(),
  versionId: z.number(),
  shortId: z.string(),
  businessPolicyVersion: z.enum(['2024-08-01', '2023-01-01']),
  creditCardFee: z.number().nonnegative().max(100),
  defaultPartnershipFee: z.number().nonnegative().max(100),
  partnershipFees: z
    .object({
      productLine: z.enum(productLineOptions).nullable(),
      assetClassId: z.number().nullable(),
      amount: z.number().nonnegative().max(100)
    })
    .refine((a) => a.assetClassId !== null || a.productLine !== null, 'Must have either productLine or assetClassId')
    .refine((a) => a.assetClassId === null || a.productLine === null, 'Cannot have both productLine and assetClassId')
    .array(),
  onlineBookingFee: z.number().nonnegative().max(100),
  nationalSiteCommission: z.number().nonnegative().max(100),
  selectBookingFee: z.number().nonnegative().max(100),
  rideInsuranceFee: z.number().nonnegative().max(100),
  weatherGuaranteeFee: z.number().nonnegative().min(80).max(100),
  chargebackFee: z.number().nonnegative().min(15_00).max(1000_00),
  partialPaymentFeeCollectionPolicy: z.enum(partialPaymentFeeCollectionPolicyOptions),
  vehiclePaymentPlanFee: z.number().nonnegative().max(100),
  createdDate: isoZ.instant()
})

const feeAgreementSystemView = feeAgreementModel

const feeAgreementCreate = feeAgreementModel.omit({
  createdDate: true,
  versionId: true,
  shortId: true
})

const FeeTypeOptions = [
  'creditCardFee',
  'chargebackFee',
  'partnershipFee',
  'onlineBookingFee',
  'nationalSiteCommission',
  'selectBookingFee',
  'rideInsuranceFee',
  'weatherGuaranteeFee',
  'vehiclePaymentPlanFee'
] as const

const LatestFeeAgreementBusinessPolicyVersion: FeeAgreementSchemas.feeAgreementSystemView['businessPolicyVersion'] =
  '2024-08-01'

/**
 * NOTE: DO NOT EDIT THE OLD IMPLEMENTATIONS OF THESE FUNCTIONS WITHOUT LONG PAINFUL DISCUSSION.
 * IN THEORY, ALL DETAILS OF THESE FUNCTIONS ARE ALSO ENSHRINED IN LENGTHY LEGAL AGREEMENTS
 * WHICH WE HAVE NO VISIBILITY INTO.
 */
const FeeAgreementBusinessPolicies = {
  ['2024-08-01'](agreement) {
    const refundableFees = [
      'partnershipFee',
      'onlineBookingFee',
      'nationalSiteCommission',
      'rideInsuranceFee',
      'weatherGuaranteeFee',
      'creditCardFee',
      'vehiclePaymentPlanFee'
    ] as FeeAgreementSchemas.FeeType[]
    return {
      getPartialPaymentFeeCollectionPolicy() {
        return agreement.partialPaymentFeeCollectionPolicy
      },
      listRefundableFees() {
        return refundableFees
      },
      getPlatformFeesForReservation(reservation) {
        if (reservation.bookingMethod === 'select') {
          return [{ unit: 'percentage', amount: agreement.selectBookingFee, refundable: true, feeType: 'partnershipFee' }]
        } else if (reservation.bookingMethod === 'viator') {
          // TODO: add viator fee
          return []
        } else {
          const partnershipFeeAmount =
            agreement.partnershipFees.find(
              (p) =>
                (p.assetClassId && p.assetClassId === reservation.assetClassId) ||
                (p.productLine && p.productLine === reservation.productLine)
            )?.amount ?? agreement.defaultPartnershipFee
          const partnershipFee = {
            unit: 'percentage' as const,
            amount: partnershipFeeAmount,
            refundable: refundableFees.includes('partnershipFee'),
            feeType: 'partnershipFee' as const
          }
          const onlineBookingFee = {
            unit: 'percentage' as const,
            amount: agreement.onlineBookingFee,
            refundable: refundableFees.includes('onlineBookingFee'),
            feeType: 'onlineBookingFee' as const
          }
          const nationalSiteCommission = {
            unit: 'percentage' as const,
            amount: agreement.nationalSiteCommission,
            refundable: refundableFees.includes('nationalSiteCommission'),
            feeType: 'nationalSiteCommission' as const
          }
          const vehiclePaymentPlanFee = {
            unit: 'percentage' as const,
            amount: agreement.vehiclePaymentPlanFee,
            refundable: refundableFees.includes('vehiclePaymentPlanFee'),
            feeType: 'vehiclePaymentPlanFee' as const
          }
          return match(reservation.bookingMethod)
            .with('adv', () => [partnershipFee, onlineBookingFee, nationalSiteCommission, vehiclePaymentPlanFee])
            .with('advWhiteLabel', () => [partnershipFee, onlineBookingFee, vehiclePaymentPlanFee])
            .with('outfitter', () => [partnershipFee, vehiclePaymentPlanFee])
            .exhaustive()
        }
      },
      getPlatformFeesForAddon(addon) {
        if (addon.metadata.type === 'SensibleWeatherGuarantee') {
          return [
            {
              amount: agreement.weatherGuaranteeFee,
              unit: 'percentage',
              refundable: refundableFees.includes('weatherGuaranteeFee'),
              feeType: 'weatherGuaranteeFee'
            }
          ]
        } else if (addon.metadata.type === 'RideInsurance' && addon.metadata.planType === 'Premium') {
          return [
            {
              amount: agreement.rideInsuranceFee,
              unit: 'percentage',
              refundable: refundableFees.includes('rideInsuranceFee'),
              feeType: 'rideInsuranceFee'
            }
          ]
        } else {
          return []
        }
      },
      getCreditCardFeeStrategy() {
        return {
          refundable: refundableFees.includes('creditCardFee'),
          type: 'creditCardFee',
          unit: 'percentage',
          amount: agreement.creditCardFee
        }
      },
      getChargebackFee() {
        return {
          refundable: refundableFees.includes('chargebackFee'),
          unit: 'fixed',
          amount: agreement.chargebackFee,
          type: 'chargebackFee' as const
        }
      }
    }
  },
  ['2023-01-01'](agreement) {
    const refundableFees = ['partnershipFee', 'creditCardFee'] as FeeAgreementSchemas.FeeType[]
    return {
      getPartialPaymentFeeCollectionPolicy() {
        return agreement.partialPaymentFeeCollectionPolicy
      },
      listRefundableFees() {
        return refundableFees
      },
      getPlatformFeesForReservation(reservation) {
        if (reservation.bookingMethod === 'select') {
          return [{ unit: 'percentage', amount: agreement.selectBookingFee, refundable: true, feeType: 'partnershipFee' }]
        } else {
          return [
            {
              unit: 'percentage',
              amount: agreement.defaultPartnershipFee,
              refundable: true,
              feeType: 'partnershipFee'
            }
          ]
        }
      },
      getPlatformFeesForAddon(addon) {
        const platformFeeMap = {
          AdHoc: 3,
          CancellationFee: 3,
          DamageFee: 3,
          RideInsurance: 0,
          Merchandise: 3,
          RentalEquipment: 3,
          SensibleWeatherGuarantee: agreement.weatherGuaranteeFee,
          GiftCard: 0
        }
        const amount: number = addon.metadata.productType
          ? platformFeeMap[addon.metadata.productType]
          : platformFeeMap[addon.metadata.type]
        return [
          {
            unit: 'percentage',
            amount: amount,
            refundable: refundableFees.includes('partnershipFee'),
            feeType: 'partnershipFee'
          }
        ]
      },
      getCreditCardFeeStrategy() {
        return {
          refundable: refundableFees.includes('creditCardFee'),
          type: 'creditCardFee',
          unit: 'percentage',
          amount: agreement.creditCardFee
        }
      },
      getChargebackFee() {
        return {
          refundable: false,
          unit: 'fixed',
          amount: agreement.chargebackFee,
          type: 'chargebackFee' as const
        }
      }
    }
  }
} satisfies {
  [key in FeeAgreementSchemas.feeAgreementSystemView['businessPolicyVersion']]: (
    agreement: FeeAgreementSchemas.feeAgreementSystemView
  ) => FeeAgreementSchemas.FeeAgreementBusinessPolicy
}

function getBusinessPolicyForFeeAgreement(
  feeAgreement: FeeAgreementSchemas.feeAgreementSystemView
): FeeAgreementSchemas.FeeAgreementBusinessPolicy {
  const version = FeeAgreementBusinessPolicies[feeAgreement.businessPolicyVersion]
  return version(feeAgreement)
}

export const FeeAgreementSchemas = {
  feeAgreementModel,
  feeAgreementSystemView,
  feeAgreementCreate,
  FeeTypeOptions,
  LatestFeeAgreementBusinessPolicyVersion,
  getBusinessPolicyForFeeAgreement,
  partialPaymentFeeCollectionPolicyOptions
}

export declare namespace FeeAgreementSchemas {
  interface FeeAgreementBusinessPolicy {
    getPartialPaymentFeeCollectionPolicy(): FeeAgreementSchemas.PartialPaymentFeeCollectionPolicy
    listRefundableFees(): FeeAgreementSchemas.FeeType[]
    getPlatformFeesForReservation(orderLine: {
      bookingMethod: CustomerOrderBookingMethod
      productLine: ProductLine | null
      assetClassId: number | null
    }): {
      unit: FeeUnit
      amount: number
      feeType: OrderLineFeeType
      refundable: boolean
    }[]
    getPlatformFeesForAddon(orderLine: {
      metadata: {
        type: AddOnType
        productType?: AddOnProductType
        planType?: RideInsuranceConfigSchemas.RideInsurancePlanType
      }
    }): {
      unit: FeeUnit
      amount: number
      feeType: OrderLineFeeType
      refundable: boolean
    }[]
    getCreditCardFeeStrategy(): {
      refundable: boolean
      unit: FeeUnit
      amount: number
      type: 'creditCardFee'
    }
    getChargebackFee(): {
      refundable: boolean
      unit: FeeUnit
      type: 'chargebackFee'
      amount: number
    }
  }
  export type feeAgreementModel = z.infer<typeof feeAgreementModel>
  export type feeAgreementSystemView = z.infer<typeof feeAgreementSystemView>
  export type feeAgreementCreate = z.infer<typeof feeAgreementCreate>

  export type FeeUnit = 'percentage' | 'fixed'
  export type FeeType = (typeof FeeTypeOptions)[number]
  export type OrderLineFeeType = Exclude<FeeType, 'creditCardFee' | 'chargebackFee'>
  export type PartialPaymentFeeCollectionPolicy = (typeof partialPaymentFeeCollectionPolicyOptions)[number]
  export type RefundTimingPolicy = { policy: 'immediate' } | { policy: 'afterPayout'; daysAfterPayout: number }
}
